import { PreviewSvgModelInstance } from '../../../store/_models/previewSvg';
import React, { useEffect } from 'react';
import { View } from 'react-native';
import { PreviewContent } from '@photosi/rn-components/preview-content/preview-content/PreviewContent';
import Datadog from '../../../_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../../_shared/datadog/DatadogCustomActions';
import { useResponsiveScale } from '../../../contexts/ResponsiveScaleContext';
import { observer } from 'mobx-react-lite';
import { LegerPreviewBackground } from './LegerPreviewBackground';
import { styles } from './LegerPreview.styles';
import { MultiPreviewContent } from '@photosi/rn-components/preview-content/multi-preview-content/MultiPreviewContent';
import { productPreviewTestID } from 'src/helpers/testIDs.helper';
import { TestIDs } from 'src/constants/testIDs.constants';
import Animated, {
  SharedValue,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

type LegerPreviewProps = {
  preview: PreviewSvgModelInstance;
  editable: boolean;
  showGrid: boolean;
  disabled?: boolean;
  swappable?: boolean;
  disableSwapImageAnimation?: boolean;
  isHighlighted?: SharedValue<boolean> | boolean;
  showBadges?: boolean;
};

const LegerPreview = observer(
  ({
    preview,
    disabled,
    editable,
    swappable,
    showGrid,
    disableSwapImageAnimation = false,
    isHighlighted = false,
    showBadges = true,
  }: LegerPreviewProps) => {
    const { responsiveScaleSV, responsiveScale } = useResponsiveScale();

    useEffect(() => {
      Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.configuration_option_apply_time);
    }, [preview?.frameUrl, preview?.backgroundUrl]);

    const borderAnimatedStyle = useAnimatedStyle(() => {
      if (typeof isHighlighted === 'object' && isHighlighted?.value !== undefined) {
        // SharedValue case - animated
        return {
          opacity: isHighlighted.value ? 1 : 0,
        };
      } else if (typeof isHighlighted === 'boolean') {
        // Boolean case - static
        return {
          opacity: isHighlighted ? 1 : 0,
        };
      }
      return {
        opacity: 0,
      };
    });
    const transformScaleSV = useSharedValue(1);
    useDerivedValue(() => {
      if (typeof isHighlighted === 'object' && isHighlighted?.value !== undefined) {
        // SharedValue case - animated
        transformScaleSV.value = withTiming(isHighlighted.value ? 0.98 : 1, { duration: 250 });
      } else if (typeof isHighlighted === 'boolean') {
        // Boolean case - static
        transformScaleSV.value = withTiming(isHighlighted ? 0.98 : 1, { duration: 250 });
      }
    }, [isHighlighted]);

    const sizeAnimatedStyle = useAnimatedStyle(
      () => ({
        width: preview.viewBox.width * responsiveScaleSV.value,
        height: preview.viewBox.height * responsiveScaleSV.value,
        transform: [{ scale: transformScaleSV.value }],
      }),
      [responsiveScaleSV, preview.viewBox.width, preview.viewBox.height, transformScaleSV, responsiveScale],
    );

    return (
      // PREVIEW CONTAINER
      <View
        style={[styles.previewContainer]}
        testID={productPreviewTestID(preview.userImageArea.productId, TestIDs.LEGER)}
      >
        <Animated.View style={[sizeAnimatedStyle]}>
          <LegerPreviewBackground viewBox={preview.innerViewBox} scaleSV={responsiveScaleSV} zIndex={0} />
          {preview.isMultiphoto ? (
            <MultiPreviewContent
              disabled={disabled}
              editable={editable}
              showGrid={showGrid}
              swappable={swappable}
              preview={preview}
              disableSwapImageAnimation={disableSwapImageAnimation}
            />
          ) : (
            <PreviewContent
              disabled={disabled}
              editable={editable}
              showGrid={showGrid}
              preview={preview}
              disableSwapImageAnimation={disableSwapImageAnimation}
              showBadges={showBadges}
            />
          )}
        </Animated.View>
        {/* HIGHLIGHTED BORDER */}
        <Animated.View style={[styles.highlighted, sizeAnimatedStyle, borderAnimatedStyle]} />
      </View>
    );
  },
);

export default LegerPreview;
